import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import LovePaidCard from "./LovePaidCard";
import { INDIA_CITIES } from "../utils/indiaLocations";
import { RAZORPAY_BRAND } from "../utils/razorpayBranding";
import "./LovePaid.css";

// =====================================================
// LOVE LEADERBOARD — ADD YOUR NAME (₹12, visible 24 hours)
//
// Payment follows the manager-provided Razorpay integration: dynamic SDK load
// → POST /api/razorpay/create-order → Checkout → success handler → POST
// /api/razorpay/verify → act only on the server's verified answer, with
// payment.failed and modal-dismiss both handled.
//
// The checks below are the FIRST line of defence, not the only one: the same
// rules (and the full word policy) run again on the server, which is what
// actually decides whether an entry is created. Nothing here can be relied on
// by an attacker skipping the UI.
// =====================================================

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const NAME_MAX = 8;
const CONTENT_MAX = 100;

// A short, obvious-cases-only mirror of the server's content policy
// (Server/utils/contentPolicy.js), so a user gets told immediately instead of
// after opening a payment window. The server list is the authoritative one.
const BLOCKED_HINT =
  /\b(sex|sexy|nude|naked|porn|xxx|boobs|penis|vagina|pussy|dick|whore|slut|rape|bitch|fuck|shit|bastard|asshole|randi|chutiya|madarchod|behenchod|gandu|harami|lund|idiot|nigger|faggot)\b/i;

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

// Profile picture — OPTIONAL. These two rules mirror the server's multer
// configuration (routes/razorpay.js), so an image the server would refuse is
// caught here instead of after an upload.
const IMAGE_MAX_BYTES = 3 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Dynamic SDK load, per the manager's implementation: reuse the global if it is
// already there, reuse a <script> another page started (a second tag for the
// same src would race), otherwise inject one.
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true));
      existingScript.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.src = CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

// One optional photo, used twice: once for the payer and once for the friend
// or lover. Each instance owns its own input, preview and Remove button, so a
// photo picked for one person can never land on the other.
function PhotoField({ id, inputRef, caption, preview, hasFile, onPick, onRemove }) {
  return (
    <div className="lpn-photo-slot">
      <label className="lpn-photo-drop" htmlFor={id}>
        {preview ? (
          <img src={preview} alt={caption} />
        ) : (
          <span className="lpn-photo-placeholder" aria-hidden="true">＋</span>
        )}
      </label>

      <span className="lpn-photo-caption">{caption}</span>

      <input
        id={id}
        ref={inputRef}
        className="lpn-photo-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />

      {hasFile && (
        <button type="button" className="lpn-photo-remove" onClick={onRemove}>
          ✕ Remove
        </button>
      )}
    </div>
  );
}

function AddYourName() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [type, setType] = useState("");
  const [content, setContent] = useState("");

  // CITY PRIDE — one main city per state, so the city alone is the whole
  // answer. It is mandatory for a new entry.
  const [city, setCity] = useState("");

  // Optional photos — one per person, kept apart so each face stays with its
  // own name. Each holds the File itself plus a local preview URL.
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const photoInputRef = useRef(null);

  const [partnerPhoto, setPartnerPhoto] = useState(null);
  const [partnerPhotoPreview, setPartnerPhotoPreview] = useState("");
  const partnerPhotoInputRef = useRef(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  const token = localStorage.getItem("token");

  // Refreshing this page after paying must not look like the payment was lost:
  // the card is rebuilt from the server, which is the only thing that knows an
  // entry is live (and whether its card was already closed).
  useEffect(() => {
    let cancelled = false;
    if (!token) return undefined;

    axios
      .get(`${BASE_URL}/api/love/paid/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const open = (Array.isArray(res.data) ? res.data : []).find((e) => !e.cardClosed);
        if (!cancelled && open) setEntry(open);
      })
      .catch(() => { /* no live entry — the form is the right thing to show */ });

    return () => { cancelled = true; };
  }, [token]);

  // The previews are object URLs, so they are released when replaced and when
  // the page unmounts — otherwise every re-pick leaks the previous image.
  useEffect(() => {
    if (!photoPreview) return undefined;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  useEffect(() => {
    if (!partnerPhotoPreview) return undefined;
    return () => URL.revokeObjectURL(partnerPhotoPreview);
  }, [partnerPhotoPreview]);

  // One handler, used by both photo fields. `which` decides whose photo is
  // being set, so the two can never be crossed.
  const photoSlots = {
    yours: { setFile: setPhoto, setPreview: setPhotoPreview, ref: photoInputRef },
    partner: {
      setFile: setPartnerPhoto,
      setPreview: setPartnerPhotoPreview,
      ref: partnerPhotoInputRef,
    },
  };

  const handlePhotoChange = (which, file) => {
    const slot = photoSlots[which];

    if (!file) {
      slot.setFile(null);
      slot.setPreview("");
      return;
    }
    // A rejected file must also drop whatever was chosen before it. Otherwise
    // the input shows the new filename while the old photo is still the one
    // that would be submitted.
    const reject = (message) => {
      setError(message);
      slot.setFile(null);
      slot.setPreview("");
      if (slot.ref.current) slot.ref.current.value = "";
    };

    if (!IMAGE_TYPES.includes(file.type)) {
      reject("Please choose a JPG, PNG or WEBP image for the photo.");
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      reject("That photo is too large. Please choose an image under 3MB.");
      return;
    }
    setError("");
    slot.setFile(file);
    slot.setPreview(URL.createObjectURL(file));
  };

  // Clearing the input's value matters: without it the browser fires no
  // `change` event when the user picks the SAME file again after removing it.
  const removePhoto = (which) => {
    const slot = photoSlots[which];
    slot.setFile(null);
    slot.setPreview("");
    if (slot.ref.current) slot.ref.current.value = "";
  };

  const validateForm = () => {
    const trimmedName = name.trim();
    const trimmedPartnerName = partnerName.trim();
    const trimmedContent = content.trim();

    if (!trimmedName) return "Please enter your name.";
    if (trimmedName.length < 2) return "Your name must be at least 2 characters.";
    if (trimmedName.length > NAME_MAX) return `Your name must be ${NAME_MAX} characters or fewer.`;
    if (!trimmedPartnerName) return "Please enter your friend's or lover's name.";
    if (trimmedPartnerName.length < 2) return "Friend or lover name must be at least 2 characters.";
    if (trimmedPartnerName.length > NAME_MAX) return `Friend or lover name must be ${NAME_MAX} characters or fewer.`;
    if (type !== "love" && type !== "friend") return "Please select Friend or Love.";
    // The entry cannot be submitted without a city — it is what puts a name on
    // the Live City Leaderboard.
    if (!city) return "Please select your City.";
    if (!trimmedContent) return "Please enter your message.";
    if (trimmedContent.length < 3) return "Your message is too short.";
    if (trimmedContent.length > CONTENT_MAX) return `Message must be ${CONTENT_MAX} characters or fewer.`;
    if (
      BLOCKED_HINT.test(trimmedName) ||
      BLOCKED_HINT.test(trimmedPartnerName) ||
      BLOCKED_HINT.test(trimmedContent)
    ) {
      return "Please keep the names and message respectful — sexual, abusive or offensive words are not allowed.";
    }
    return "";
  };

  // PAYMENT SUCCESS HANDLER → server-side verification.
  // Checkout calling this handler is NOT proof of payment: the name is only
  // shown as added once /api/razorpay/verify confirms the signature.
  const verifyPayment = async (paymentResponse) => {
    try {
      const verifyResponse = await axios.post(
        `${BASE_URL}/api/razorpay/verify`,
        {
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature: paymentResponse.razorpay_signature,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        }
      );

      const data = verifyResponse.data;

      if (data?.success && data.entry) {
        setEntry(data.entry);
        setSuccess("Payment successful! Your name has been added to the Live Leaderboard for 24 hours.");
        setError("");
        setName("");
        setPartnerName("");
        setType("");
        setContent("");
        setCity("");
        setPhoto(null);
        setPhotoPreview("");
        setPartnerPhoto(null);
        setPartnerPhotoPreview("");
        if (photoInputRef.current) photoInputRef.current.value = "";
        if (partnerPhotoInputRef.current) partnerPhotoInputRef.current.value = "";
      } else {
        setError(data?.message || "Payment verification failed. Your name was not added.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Payment verification failed. If money was deducted, please contact the team."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (loading) return;

    const problem = validateForm();
    if (problem) {
      setError(problem);
      setSuccess("");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      // ── 1. LOAD RAZORPAY SDK ──
      const razorpayLoaded = await loadRazorpayScript();
      if (!razorpayLoaded || !window.Razorpay) {
        throw new Error("Could not load the payment window. Please check your connection and try again.");
      }

      // ── 2. CREATE ORDER ──
      // The form travels with the order, so the server fixes both the ₹12 price
      // and the text that will be published before Checkout ever opens.
      const fields = {
        name: name.trim(),
        partnerName: partnerName.trim(),
        type,
        content: content.trim(),
        city,
      };

      // No photo → the request is exactly the JSON it has always been. A photo
      // has to ride along as multipart, and the browser sets that content type
      // itself (including the boundary), so it must not be forced here. Each
      // photo goes in its own named field, which is what keeps a face with its
      // own name.
      let payload = fields;
      let contentType = "application/json";

      if (photo || partnerPhoto) {
        const form = new FormData();
        Object.entries(fields).forEach(([key, value]) => form.append(key, value));
        if (photo) form.append("profileImage", photo);
        if (partnerPhoto) form.append("partnerImage", partnerPhoto);
        payload = form;
        contentType = undefined;
      }

      const response = await axios.post(
        `${BASE_URL}/api/razorpay/create-order`,
        payload,
        {
          headers: {
            ...(contentType ? { "Content-Type": contentType } : {}),
            Authorization: `Bearer ${token}`,
          },
          timeout: 60000,
        }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to create the payment order.");
      }

      const { order, key_id } = response.data;

      if (!key_id) throw new Error("Payment could not start (missing key). Please try again.");
      if (!order?.id) throw new Error("Payment could not start (missing order). Please try again.");
      if (!order?.amount) throw new Error("Payment could not start (missing amount). Please try again.");

      // ── 3. RAZORPAY OPTIONS ──
      const options = {
        key: key_id, // the PUBLIC key id — the secret never leaves the server
        amount: order.amount,
        currency: order.currency || "INR",
        order_id: order.id,

        ...RAZORPAY_BRAND,
        description: "Love Leaderboard — Add Your Name (24 hours)",

        handler: verifyPayment,

        prefill: {
          name: localStorage.getItem("username") || "",
          email: localStorage.getItem("email") || "",
        },

        notes: { product: "Love Leaderboard — Add Your Name" },

        theme: { color: "#f43f5e" },

        // CHECKOUT CLOSED without paying.
        modal: {
          ondismiss: () => {
            setLoading(false);
            setError("Payment was cancelled. Your name has not been added — you can try again.");
          },
        },
      };

      // ── 4. OPEN CHECKOUT ──
      const razorpay = new window.Razorpay(options);

      // PAYMENT FAILED at the gateway.
      razorpay.on("payment.failed", (failure) => {
        setLoading(false);
        setError(
          failure?.error?.description ||
            "Payment failed. Your name has not been added — please try again."
        );
      });

      razorpay.open();
    } catch (err) {
      setLoading(false);
      setError(
        err.response?.data?.message || err.message || "Could not start the payment. Please try again."
      );
    }
  };

  // Close hides the card only. The entry, the name on the leaderboard and the
  // 24-hour window all stay exactly as they are.
  const handleCloseCard = async () => {
    if (!entry) return;
    setClosing(true);
    try {
      await axios.patch(
        `${BASE_URL}/api/love/paid/${entry._id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // A failed preference update must not trap the user behind the card.
    } finally {
      setClosing(false);
      navigate("/lovelist");
    }
  };

  return (
    <>
      <div className="lpn-bg" />
      <Navbar />

      <div className="lpn-wrapper">
        <div className="lpn-page">
          <div className="lpn-head">
            <div className="lpn-badge">✦ Featured Entry</div>
            <h1 className="lpn-title">
              Add Your Name – <span>₹12</span>
            </h1>
            <p className="lpn-notice">
              ⏳ Your name will be visible on the Live Leaderboard for <strong>24 hours only</strong>.
              After 24 hours it disappears automatically.
            </p>
          </div>

          {error && <div className="lpn-error">{error}</div>}
          {success && <div className="lpn-success">🎉 {success}</div>}

          {/* After a verified payment the card replaces the form. */}
          {entry ? (
            <>
              <LovePaidCard entry={entry} onClose={handleCloseCard} closing={closing} />
              <button type="button" className="lpn-back-link" onClick={() => navigate("/lovelist")}>
                ← Back to Live Leaderboard
              </button>
            </>
          ) : (
            <>
              <div className="lpn-panel">
                <h2 className="lpn-panel-title">📜 Rules — please read</h2>
                <ul className="lpn-rules">
                  <li>No sexual or explicit content.</li>
                  <li>No sexual names.</li>
                  <li>No abusive names.</li>
                  <li>No offensive content of any kind.</li>
                  <li>Anything against TheBlogsSphere platform rules will not be accepted.</li>
                  <li>Your entry stays on the Live Leaderboard for exactly 24 hours.</li>
                  <li>The ₹12 fee is for the 24-hour feature and is non-refundable.</li>
                </ul>
              </div>

              <div className="lpn-panel">
                <h2 className="lpn-panel-title">💫 Your details</h2>

                <div className="lpn-field">
                  <label className="lpn-label" htmlFor="lpn-name">
                    Your Name <em>(shown on the leaderboard)</em>
                  </label>
                  <input
                    id="lpn-name"
                    className="lpn-input"
                    type="text"
                    placeholder="Enter your name"
                    value={name}
                    maxLength={NAME_MAX}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <span className="lpn-count">{name.length}/{NAME_MAX}</span>
                </div>

                <div className="lpn-field">
                  <label className="lpn-label" htmlFor="lpn-partner-name">
                    Friend or Lover Name <em>(shown on the leaderboard)</em>
                  </label>
                  <input
                    id="lpn-partner-name"
                    className="lpn-input"
                    type="text"
                    placeholder="Enter your friend's or lover's name"
                    value={partnerName}
                    maxLength={NAME_MAX}
                    onChange={(e) => setPartnerName(e.target.value)}
                  />
                  <span className="lpn-count">{partnerName.length}/{NAME_MAX}</span>
                </div>

                <div className="lpn-field">
                  <span className="lpn-label">Friend or Love</span>
                  <div className="lpn-choices">
                    <label className={`lpn-choice ${type === "friend" ? "lpn-choice-on-friend" : ""}`}>
                      <input
                        type="radio"
                        name="lpn-type"
                        value="friend"
                        checked={type === "friend"}
                        onChange={() => setType("friend")}
                      />
                      🤝 Friend
                    </label>
                    <label className={`lpn-choice ${type === "love" ? "lpn-choice-on-love" : ""}`}>
                      <input
                        type="radio"
                        name="lpn-type"
                        value="love"
                        checked={type === "love"}
                        onChange={() => setType("love")}
                      />
                      ❤️ Love
                    </label>
                  </div>
                </div>

                {/* ── CITY PRIDE — one main city per state, so the city is the
                    whole answer. No State dropdown. ── */}
                <div className="lpn-field">
                  <label className="lpn-label" htmlFor="lpn-city">
                    City <em>(required — this is the city you play for)</em>
                  </label>
                  <select
                    id="lpn-city"
                    className="lpn-input lpn-select"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  >
                    <option value="">Select your City</option>
                    {INDIA_CITIES.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <span className="lpn-count lpn-city-hint">
                    🏆 Your entry adds +1 to {city || "your city"} on the Live City Leaderboard.
                  </span>
                </div>

                {/* ── OPTIONAL PHOTOS — one per person, shown under that
                    person's name on the leaderboard card. ── */}
                <div className="lpn-field">
                  <span className="lpn-label">
                    Photos <em>(optional — add one, both or neither)</em>
                  </span>

                  <div className="lpn-photo-grid">
                    <PhotoField
                      id="lpn-photo"
                      inputRef={photoInputRef}
                      caption={name.trim() || "Your photo"}
                      preview={photoPreview}
                      hasFile={Boolean(photo)}
                      onPick={(file) => handlePhotoChange("yours", file)}
                      onRemove={() => removePhoto("yours")}
                    />

                    <PhotoField
                      id="lpn-partner-photo"
                      inputRef={partnerPhotoInputRef}
                      caption={partnerName.trim() || "Friend / Lover photo"}
                      preview={partnerPhotoPreview}
                      hasFile={Boolean(partnerPhoto)}
                      onPick={(file) => handlePhotoChange("partner", file)}
                      onRemove={() => removePhoto("partner")}
                    />
                  </div>

                  <span className="lpn-photo-note">
                    JPG, PNG or WEBP · up to 3MB each. Each photo appears under that
                    person's name — you can add your name without any photo.
                  </span>
                </div>

                <div className="lpn-field">
                  <label className="lpn-label" htmlFor="lpn-content">
                    Content <em>(your message)</em>
                  </label>
                  <textarea
                    id="lpn-content"
                    className="lpn-textarea"
                    placeholder="Write your message for the leaderboard"
                    value={content}
                    maxLength={CONTENT_MAX}
                    rows={4}
                    onChange={(e) => setContent(e.target.value)}
                  />
                  <span className="lpn-count">{content.length}/{CONTENT_MAX}</span>
                </div>

                <button type="button" className="lpn-pay-btn" onClick={handlePay} disabled={loading}>
                  {loading ? "Processing…" : "Pay ₹12 & Add My Name"}
                </button>
              </div>

              <button type="button" className="lpn-back-link" onClick={() => navigate("/lovelist")}>
                ← Back to Live Leaderboard
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default AddYourName;
